import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, Buildings, CaretRight, DoorOpen, House } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@/lib/api";

export default function BrowsePage({ mode }) {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [properties, setProperties] = useState([]);
    const [selected, setSelected] = useState(null);
    const [rooms, setRooms] = useState([]);
    const [loading, setLoading] = useState(false);

    const openProperty = async (p) => {
        setSelected(p);
        setLoading(true);
        try {
            const res = await api.get(`/browse/properties/${p.id}/rooms`);
            setRooms(res.data);
        } catch {
            toast.error("Gagal memuat daftar ruangan");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        api.get("/browse/properties")
            .then((res) => {
                setProperties(res.data);
                const propId = searchParams.get("prop");
                const p = propId && res.data.find((x) => x.id === propId);
                if (p) openProperty(p);
            })
            .catch(() => toast.error("Gagal memuat daftar properti"));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const openRoom = (r) => navigate(mode === "admin" ? `/a/${r.id}` : `/s/${r.id}`);

    return (
        <div className="min-h-screen" data-testid="browse-page">
            <header className="sticky top-0 z-10 bg-white/80 backdrop-blur-xl border-b border-stone-200">
                <div className="max-w-md mx-auto px-6 py-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <img src="/logo.png" alt="INERRE interior" className="h-9 object-contain" data-testid="browse-logo" />
                        <p className="text-[10px] tracking-[0.2em] uppercase font-bold text-muted-foreground">
                            {mode === "admin" ? "Mode Admin" : "Mode Supervisor"}
                        </p>
                    </div>
                    <button
                        data-testid="browse-home-link"
                        onClick={() => navigate(`/${mode}`)}
                        className="text-muted-foreground hover:text-foreground transition-colors"
                        title="Kembali ke pemindai"
                    >
                        <House size={20} weight="light" />
                    </button>
                </div>
            </header>

            <main className="max-w-md mx-auto px-6 py-8 fade-up">
                {!selected ? (
                    <>
                        <h1 className="font-serif text-3xl sm:text-4xl tracking-tighter font-medium">Semua Properti</h1>
                        <p className="text-sm text-muted-foreground mt-2 mb-6">
                            Pilih properti untuk melihat daftar ruangannya.
                        </p>
                        <div className="space-y-2" data-testid="browse-property-list">
                            {properties.map((p) => (
                                <button
                                    key={p.id}
                                    data-testid={`browse-property-${p.id}`}
                                    onClick={() => openProperty(p)}
                                    className="w-full flex items-center gap-4 border border-border bg-white p-4 text-left hover:border-primary transition-colors"
                                >
                                    <Buildings size={24} weight="light" className="text-primary shrink-0" />
                                    <span className="flex-1 min-w-0">
                                        <span className="block font-medium text-sm truncate">{p.name}</span>
                                        <span className="block text-xs text-muted-foreground">{p.room_count} ruangan</span>
                                    </span>
                                    <CaretRight size={16} weight="light" className="text-muted-foreground shrink-0" />
                                </button>
                            ))}
                            {properties.length === 0 && (
                                <p className="text-sm text-muted-foreground" data-testid="browse-properties-empty">
                                    Belum ada properti.
                                </p>
                            )}
                        </div>
                    </>
                ) : (
                    <>
                        <button
                            data-testid="browse-back-btn"
                            onClick={() => setSelected(null)}
                            className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors mb-4"
                        >
                            <ArrowLeft size={16} weight="light" /> Semua Properti
                        </button>
                        <h1 className="font-serif text-3xl sm:text-4xl tracking-tighter font-medium" data-testid="browse-property-name">
                            {selected.name}
                        </h1>
                        <p className="text-sm text-muted-foreground mt-2 mb-6">Pilih ruangan.</p>
                        {loading ? (
                            <p className="text-sm text-muted-foreground">Memuat...</p>
                        ) : (
                            <div className="space-y-2" data-testid="browse-room-list">
                                {rooms.map((r) => (
                                    <button
                                        key={r.id}
                                        data-testid={`browse-room-${r.id}`}
                                        onClick={() => openRoom(r)}
                                        className="w-full flex items-center gap-4 border border-border bg-white p-4 text-left hover:border-primary transition-colors"
                                    >
                                        <DoorOpen size={24} weight="light" className="text-primary shrink-0" />
                                        <span className="flex-1 min-w-0">
                                            <span className="block font-medium text-sm truncate">{r.name}</span>
                                            {r.short_code && (
                                                <span className="block text-xs text-muted-foreground font-mono tracking-[0.2em]">{r.short_code}</span>
                                            )}
                                        </span>
                                        <CaretRight size={16} weight="light" className="text-muted-foreground shrink-0" />
                                    </button>
                                ))}
                                {rooms.length === 0 && (
                                    <p className="text-sm text-muted-foreground" data-testid="browse-rooms-empty">
                                        Belum ada ruangan.
                                    </p>
                                )}
                            </div>
                        )}
                    </>
                )}
            </main>
        </div>
    );
}
